// Storefront analytics tracker. Sends pageviews on storefront routes, every
// cart change (add / remove / quantity) and premium-label QR scans to
// POST /api/jungle/track; the admin dashboard lives at /admin/analytics.
//
// Cart changes are detected by diffing the cart store, so the many components
// that edit $cart.items (product page, drawer, cart page, "Clear") are all
// covered without touching them. Only the checkout — which empties the cart —
// must call trackCheckout() first so that emptying isn't logged as removals.
//
// Staff devices never report: once any staff/admin login is seen on a browser
// it is marked and stays silent even after logout (POS kiosks, admin phones).
import { get } from "svelte/store";
import app from "@/store/app.js";
import cart from "@/store/cart.js";
import user from "@/store/user.js";
import tenant from "@/store/tenant.js";
import { url as pbUrl, client } from "@/assets/js/pocketbase";

const CID_KEY = "jh_cid";
const SESSION_KEY = "jh_session";
const STAFF_KEY = "jh_staff_device";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 2500;

// Public storefront routes worth counting. Everything else (POS, admin, TV, labels) is ignored.
const STOREFRONT = [
    /^\/$/, /^\/product\/[^/]+$/, /^\/products(\/[^/]+)?$/, /^\/cart$/, /^\/checkout$/, /^\/payment$/, /^\/confirmation$/,
    /^\/about$/, /^\/find-us$/, /^\/blog(\/[^/]+)?$/, /^\/weed-(delivery|shop)-[a-z]+$/, /^\/login$/, /^\/signup$/, /^\/account$/,
];

// Premium bag labels carry a QR to /l/<code> (code = labelCodeOf(cart line),
// also stored on the sale line). The code must never stay in the address bar, so
// captureLabelScan() runs before the app mounts: it keeps the code and swaps the
// URL for the flower menu (replaceState — Back never returns to /l/…). The scan
// is sent once the tracker starts and the tenant is known.
const LABEL_SCAN_RE = /^\/l\/([a-z0-9]{6,16})\/?$/i;
const LABEL_LANDING = "/products?category=Flower";
let pendingLabelCode = "";

export function captureLabelScan() {
    if (typeof window === "undefined") return;
    const m = LABEL_SCAN_RE.exec(location.pathname);
    if (!m) return;
    pendingLabelCode = m[1].toLowerCase();
    try { history.replaceState(null, "", LABEL_LANDING); } catch {}
}

let started = false;
let queue = [];
let flushTimer = null;
let fingerprint = "";
let referrerSent = false;
let suppressCartDiff = false;

function safeGet(store, key) {
    try { return store.getItem(key); } catch { return null; }
}
function safeSet(store, key, val) {
    try { store.setItem(key, val); } catch {}
}

function randomId(len = 20) {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const bytes = new Uint8Array(len);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

function clientId() {
    let id = safeGet(localStorage, CID_KEY);
    if (!id || !/^[a-z0-9]{8,40}$/.test(id)) {
        id = randomId();
        safeSet(localStorage, CID_KEY, id);
    }
    return id;
}

// A visit ends after 30 minutes of inactivity.
function sessionId() {
    const now = Date.now();
    let s = null;
    try { s = JSON.parse(safeGet(localStorage, SESSION_KEY) || "null"); } catch {}
    if (!s || !s.id || now - (s.last || 0) > SESSION_IDLE_MS) {
        s = { id: randomId(16), last: now };
    }
    s.last = now;
    safeSet(localStorage, SESSION_KEY, JSON.stringify(s));
    return s.id;
}

// 53-bit string hash (cyrb53) — no crypto.subtle so it also works on plain-http dev hosts.
function hash(str) {
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

// Device fingerprint from traits that survive clearing storage / incognito:
// screen, GPU, canvas rendering, timezone, language, CPU/memory. Deliberately
// leaves out things that drift on one device (zoom/devicePixelRatio, emoji
// glyphs that render differently before the emoji font has loaded). Background
// tabs report a 0x0 screen, so it is only computed once the page is visible.
function computeFingerprint() {
    const parts = [];
    const push = (v) => parts.push(v == null ? "" : String(v));
    try {
        // Sorted so rotating a phone doesn't change it.
        const dims = [screen.width, screen.height].sort((a, b) => a - b);
        push(dims.join("x") + "x" + screen.colorDepth);
        push(Intl.DateTimeFormat().resolvedOptions().timeZone);
        push((navigator.languages || [navigator.language]).join(","));
        push(navigator.platform);
        push(navigator.hardwareConcurrency);
        push(navigator.deviceMemory);
        push(navigator.maxTouchPoints);
    } catch {}
    try {
        const c = document.createElement("canvas");
        c.width = 240;
        c.height = 60;
        const ctx = c.getContext("2d");
        ctx.textBaseline = "top";
        ctx.font = "16px 'Arial'";
        ctx.fillStyle = "#f60";
        ctx.fillRect(100, 1, 62, 20);
        ctx.fillStyle = "#069";
        ctx.fillText("JungleHouse fp,<canvas> 1.0 Cwm", 2, 15);
        ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
        ctx.fillText("JungleHouse fp,<canvas> 1.0 Cwm", 4, 17);
        push(hash(c.toDataURL()));
    } catch {}
    try {
        const gl = document.createElement("canvas").getContext("webgl");
        const dbg = gl && gl.getExtension("WEBGL_debug_renderer_info");
        if (dbg) {
            push(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL));
            push(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL));
        }
    } catch {}
    return hash(parts.join("|"));
}

function isStaffDevice() {
    return safeGet(localStorage, STAFF_KEY) === "1";
}

function enabled() {
    return started && !isStaffDevice() && !navigator.webdriver && !!get(tenant)?.id;
}

function enqueue(ev) {
    if (!enabled()) return;
    queue.push(ev);
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
}

function flush() {
    clearTimeout(flushTimer);
    flushTimer = null;
    if (!queue.length || !enabled()) {
        queue = [];
        return;
    }
    if (!fingerprint && screen.width > 0) {
        try { fingerprint = computeFingerprint(); } catch {}
    }
    const events = queue.splice(0, 40);
    const headers = { "Content-Type": "application/json" };
    // Lets the server recognise logged-in shoppers (and drop staff).
    if (client.authStore?.isValid && client.authStore.token) headers.Authorization = client.authStore.token;
    let tz = "";
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch {}
    const body = JSON.stringify({
        tenant: get(tenant).id,
        cid: clientId(),
        session: sessionId(),
        fp: fingerprint,
        screen: `${screen.width}x${screen.height}`,
        lang: navigator.language || "",
        tz,
        events,
    });
    // keepalive lets the request finish while the tab closes.
    fetch(`${pbUrl}api/jungle/track`, { method: "POST", headers, body, keepalive: true }).catch(() => {});
    if (queue.length) flushTimer = setTimeout(flush, 200);
}

function referrerHost() {
    if (referrerSent) return "";
    referrerSent = true;
    try {
        if (!document.referrer) return "";
        const r = new URL(document.referrer);
        if (r.hostname === location.hostname) return "";
        return r.hostname.replace(/^www\./, "");
    } catch {
        return "";
    }
}

function trackPageview(path) {
    const pathname = path.split("?")[0].replace(/(.)\/+$/, "$1");
    if (!STOREFRONT.some((re) => re.test(pathname))) return;
    enqueue({ type: "pageview", path, referrer: referrerHost() });
}

function lineOf(item) {
    const isInventory = item.category === "inventory" || item.itemType === "inventory";
    return {
        productId: item.id,
        productName: (isInventory ? item.name : item.strain) || item.name || item.strain || "",
        productKind: isInventory ? "inventory" : "flower",
        price: Number(isInventory ? item.price : item.customerPrice) || 0,
        qty: Number(item.quantity) || 0,
    };
}

function cartMap(items) {
    const m = new Map();
    for (const it of items || []) if (it && it.id) m.set(it.id, lineOf(it));
    return m;
}

function diffCart(prev, next) {
    const path = get(app)?.url || location.pathname;
    for (const [id, line] of next) {
        const before = prev.get(id);
        if (!before) enqueue({ type: "cart_add", path, ...line });
        else if (line.qty !== before.qty) enqueue({ type: "cart_qty", path, ...line, qty: line.qty - before.qty });
    }
    for (const [id, line] of prev) {
        if (!next.has(id)) enqueue({ type: "cart_remove", path, ...line });
    }
}

// Call right before the cart is emptied by a completed order.
export function trackCheckout(items, extra = {}) {
    const lines = (items || []).map(lineOf);
    enqueue({
        type: "checkout",
        path: get(app)?.url || location.pathname,
        qty: lines.reduce((n, l) => n + l.qty, 0),
        price: Number(extra.total) || 0,
        items: lines.map((l) => ({ id: l.productId, name: l.productName, qty: l.qty, price: l.price })),
    });
    // The cart is cleared shortly after (see handleWhatsappOrder); don't log that as removals.
    suppressCartDiff = true;
    setTimeout(() => (suppressCartDiff = false), 3000);
    flush();
}

export function initSiteAnalytics() {
    if (started || typeof window === "undefined") return;
    started = true;

    user.subscribe((u) => {
        if (u?.id && u.role && u.role !== "customer") safeSet(localStorage, STAFF_KEY, "1");
    });

    // The persisted app store first replays the LAST session's url; only count
    // it if it really is the page we're on (the router re-sets it on mount).
    let firstUrl = true;
    let lastPath = null;
    app.subscribe((a) => {
        const path = a?.url;
        if (firstUrl) {
            firstUrl = false;
            if (path !== location.pathname + location.search) return;
        }
        if (!path || path === lastPath) return;
        lastPath = path;
        trackPageview(path);
    });

    let prev = null;
    cart.subscribe((c) => {
        const next = cartMap(c?.items);
        if (prev && !suppressCartDiff) diffCart(prev, next);
        prev = next;
    });

    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);

    // Runs after initTenant (App.svelte), so the tenant id is already known. Sent
    // straight away rather than batched: the scan is the one event we can't lose.
    if (pendingLabelCode) {
        enqueue({ type: "label_scan", path: LABEL_LANDING, productId: pendingLabelCode, productKind: "label" });
        pendingLabelCode = "";
        flush();
    }
}
